import type { TFunction } from 'i18next'
import type {
  ProcessedEntrance,
  ProcessedFloor,
  ProcessedUnit,
  UnitPricePrediction,
} from './types'

/**
 * Groups dwellings by their floor number.
 *
 * Floors can be negative: the RegBL/GWR reports basement dwellings as floor -1,
 * -2, ... Those buckets must survive all the way to the rendered floor list.
 */
const groupDwellingsByFloor = (dwellings: any[]): Record<number, any[]> => {
  return dwellings.reduce(
    (acc: Record<number, any[]>, dwelling: any) => {
      const floorNum = dwelling.floor ?? 0
      if (!acc[floorNum]) {
        acc[floorNum] = []
      }
      acc[floorNum].push(dwelling)
      return acc
    },
    {} as Record<number, any[]>
  )
}

/**
 * Builds the ordered list of floor numbers to render for an entrance.
 *
 * The RegBL floor count spans basements, so the range cannot be assumed to
 * start at ground level: a building reported with 2 floors may well have its
 * dwellings on floors -1 and 0 rather than 0 and 1.
 *
 * - every occupied floor gets a row, so no dwelling is ever dropped
 * - below ground, only occupied levels are emitted (padding would explode on
 *   the sentinel values, e.g. floor -9, present in the federal data)
 * - above ground, empty floors are still padded up to the reported floor count,
 *   minus the levels the basements already account for
 */
const calculateFloorNumbers = (
  dwellings: any[],
  buildingAdmin: any,
  building: any
): number[] => {
  const occupiedFloors = Array.from(
    new Set(dwellings.map((dwelling: any) => dwelling.floor ?? 0))
  ).sort((a, b) => a - b)

  const basementFloors = occupiedFloors.filter((floor) => floor < 0)
  const aboveGroundFloors = occupiedFloors.filter((floor) => floor >= 0)

  const floorCount = buildingAdmin.floor_nb || building.floor_nb || 0
  const remainingBudget = floorCount - 1 - basementFloors.length

  const topFloor = Math.max(
    aboveGroundFloors.length > 0 ? Math.max(...aboveGroundFloors) : -1,
    remainingBudget
  )

  const floors = [...basementFloors]
  for (let floorNum = 0; floorNum <= topFloor; floorNum++) {
    floors.push(floorNum)
  }

  return floors
}

/**
 * Creates a ProcessedUnit from dwelling data
 */
const createProcessedUnit = (
  dwelling: any,
  unitIndex: number,
  floorNum: number,
  entranceId: string,
  address: any,
  buildingAdmin: any,
  t: TFunction
): ProcessedUnit => {
  const roomNb = dwelling.room_nb || 0
  const roomsLabel =
    roomNb === 1
      ? t('unitDetails.unitTypes.roomCount', { count: roomNb })
      : t('unitDetails.unitTypes.roomsCount', { count: roomNb })

  // Extract structured price prediction
  const pricePrediction: UnitPricePrediction = {}
  const pp = dwelling.price_prediction

  if (
    pp?.purchase?.price &&
    pp.purchase.lower_bound &&
    pp.purchase.upper_bound
  ) {
    pricePrediction.purchase = {
      price: pp.purchase.price,
      lowerBound: pp.purchase.lower_bound,
      upperBound: pp.purchase.upper_bound,
    }
  }

  if (pp?.rent?.price && pp.rent.lower_bound && pp.rent.upper_bound) {
    pricePrediction.rent = {
      price: pp.rent.price,
      lowerBound: pp.rent.lower_bound,
      upperBound: pp.rent.upper_bound,
    }
  }

  return {
    id: BigInt(
      dwelling.id ||
        dwelling.dwelling_id ||
        `${entranceId}${floorNum}${unitIndex}`
    ),
    ewid: dwelling.dwelling_id || dwelling.id || 0,
    addressId: BigInt(address.id || 0),
    floorLocation: `${floorNum}.${unitIndex + 1}`,
    status: dwelling.status === 'existant' ? 'active' : 'vacant',
    administrativeNb: `${address.street_nb || ''}-${floorNum}.${unitIndex + 1}`,
    physicalNb: `${floorNum}${String.fromCharCode(65 + unitIndex)}`,
    multipleFloor: dwelling.multiple_floor ?? false,
    active: dwelling.status === 'existant',
    sourceId: 'dwelling-data',
    floor: floorNum,
    area: dwelling.area || 0,
    constructionDate:
      dwelling.building_year || buildingAdmin.building_year || undefined,
    hasKitchen: dwelling.has_kitchen,
    roomNb: roomNb,
    bathrooms: 0,
    egid: BigInt(buildingAdmin.egid || 0),
    edid: address.edid,
    manualUpdateTimestamp: new Date(),
    displayId: `${floorNum}.${unitIndex + 1}`,
    typeDisplay: roomsLabel,
    areaDisplay: `${dwelling.area || 0}${t('unitDetails.common.squareMeters')}`,
    roomsDisplay: `${roomNb}`,
    rentalEstimate: pp?.rent?.price
      ? `${t('unitDetails.common.chf')} ${Math.round(pp.rent.lower_bound)} - ${t('unitDetails.common.chf')} ${Math.round(pp.rent.upper_bound)}`
      : t('unitDetails.common.na'),
    purchaseEstimate: pp?.purchase?.price
      ? `${t('unitDetails.common.chf')} ${Math.round(pp.purchase.lower_bound)} - ${t('unitDetails.common.chf')} ${Math.round(pp.purchase.upper_bound)}`
      : undefined,
    pricePrediction:
      pricePrediction.purchase || pricePrediction.rent
        ? pricePrediction
        : undefined,
  }
}

/**
 * Creates a ProcessedFloor with units
 */
const createProcessedFloor = (
  floorNum: number,
  entranceId: string,
  dwellings: any[],
  address: any,
  buildingAdmin: any,
  t: TFunction
): ProcessedFloor => {
  const floorName =
    floorNum === 0
      ? t('unitDetails.floor.groundFloor')
      : floorNum < 0
        ? t('unitDetails.floor.basement', { level: Math.abs(floorNum) })
        : t('unitDetails.floor.floorNumber', { number: floorNum })

  const floor: ProcessedFloor = {
    id: `floor-${entranceId}-${floorNum}`,
    name: floorName,
    number: floorNum,
    type: floorNum === 0 ? 'commercial' : 'residential',
    units: [],
    totalUnits: dwellings.length,
    totalArea: 0,
  }

  dwellings.forEach((dwelling: any, unitIndex: number) => {
    const unit = createProcessedUnit(
      dwelling,
      unitIndex,
      floorNum,
      entranceId,
      address,
      buildingAdmin,
      t
    )
    floor.units.push(unit)
  })

  floor.totalArea = floor.units.reduce((sum, unit) => sum + (unit.area || 0), 0)
  return floor
}

/**
 * Creates a ProcessedEntrance from building administrative data
 */
const createProcessedEntrance = (
  buildingAdmin: any,
  adminIndex: number,
  address: any,
  addressIndex: number,
  building: any,
  t: TFunction
): ProcessedEntrance => {
  const dwellings = address.dwellings || []

  const entranceTitle =
    addressIndex === 0 && adminIndex === 0
      ? t('unitDetails.entrance.mainEntrance')
      : `${address.street} ${address.street_nb}`

  const entrance: ProcessedEntrance = {
    id: `${buildingAdmin.egid || adminIndex}-${address.id || addressIndex}`,
    title: entranceTitle,
    address: `${address.street} ${address.street_nb}, ${address.postal_code} ${address.city}`,
    egid: buildingAdmin.egid,
    floors: [],
    totalUnits: dwellings.length,
    totalArea: dwellings.reduce(
      (sum: number, dwelling: any) => sum + (dwelling.area || 0),
      0
    ),
  }

  const dwellingsByFloor = groupDwellingsByFloor(dwellings)
  const floorNumbers = calculateFloorNumbers(dwellings, buildingAdmin, building)

  for (const floorNum of floorNumbers) {
    const floorDwellings = dwellingsByFloor[floorNum] || []
    const floor = createProcessedFloor(
      floorNum,
      entrance.id,
      floorDwellings,
      address,
      buildingAdmin,
      t
    )
    entrance.floors.push(floor)
  }

  return entrance
}

/**
 * Processes building data into structured entrance/floor/unit format
 */
export const processBuildingData = (
  building: any,
  t: TFunction
): ProcessedEntrance[] | null => {
  if (
    !building?.buildings_administrative ||
    !Array.isArray(building.buildings_administrative)
  ) {
    return null
  }

  const entrances: ProcessedEntrance[] = []

  building.buildings_administrative.forEach(
    (buildingAdmin: any, adminIndex: number) => {
      buildingAdmin.addresses?.forEach((address: any, addressIndex: number) => {
        const entrance = createProcessedEntrance(
          buildingAdmin,
          adminIndex,
          address,
          addressIndex,
          building,
          t
        )
        entrances.push(entrance)
      })
    }
  )

  return entrances
}

/**
 * Calculates summary statistics from processed data
 */
export const calculateSummary = (
  building: any,
  processedData: ProcessedEntrance[] | null
) => {
  if (!processedData) {
    return {
      dwellingArea:
        building?.buildings_administrative?.[0]?.residential_area || 0,
      units: building?.unit_nb || 0,
      yearlyRentalMin: 0,
      yearlyRentalMax: 0,
      purchaseMin: 0,
      purchaseMax: 0,
    }
  }

  let yearlyRentalMin = 0
  let yearlyRentalMax = 0
  let purchaseMin = 0
  let purchaseMax = 0

  building?.buildings_administrative?.forEach((admin: any) => {
    admin.addresses?.forEach((address: any) => {
      address.dwellings?.forEach((dwelling: any) => {
        const rent = dwelling.price_prediction?.rent
        if (rent?.lower_bound && rent?.upper_bound) {
          yearlyRentalMin += rent.lower_bound
          yearlyRentalMax += rent.upper_bound
        }
        const purchase = dwelling.price_prediction?.purchase
        if (purchase?.lower_bound && purchase?.upper_bound) {
          purchaseMin += purchase.lower_bound
          purchaseMax += purchase.upper_bound
        }
      })
    })
  })

  yearlyRentalMin *= 12
  yearlyRentalMax *= 12

  return {
    dwellingArea: processedData.reduce(
      (sum, entrance) => sum + entrance.totalArea,
      0
    ),
    units: processedData.reduce(
      (sum, entrance) => sum + entrance.totalUnits,
      0
    ),
    yearlyRentalMin,
    yearlyRentalMax,
    purchaseMin,
    purchaseMax,
  }
}

/**
 * Toggle utility for Set management
 */
export const toggleSetItem = <T>(set: Set<T>, item: T): Set<T> => {
  const newSet = new Set(set)
  if (newSet.has(item)) {
    newSet.delete(item)
  } else {
    newSet.add(item)
  }
  return newSet
}
